import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Link, useBlocker, useNavigate } from "react-router";
import { ApiError } from "./api";
import { formatGBP } from "../shared/money";
import { displayReg } from "../shared/labels";

// ───────── Toasts ─────────
type Toast = { id: number; kind: "success" | "error" | "info"; text: string };
const ToastCtx = createContext<(kind: Toast["kind"], text: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast["kind"], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 9000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span>{t.text}</span>
            <button aria-label="Dismiss" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}>×</button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ───────── Basic building blocks ─────────
export function Button({ variant = "secondary", size, loading, children, className = "", ...rest }: { variant?: "primary" | "secondary" | "ghost" | "danger" | "accent"; size?: "sm"; loading?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={`btn btn-${variant} ${size === "sm" ? "btn-sm" : ""} ${className}`} disabled={loading || rest.disabled} aria-busy={loading || undefined} {...rest}>
      {loading && <span className="spin" aria-hidden />}
      {children}
    </button>
  );
}

// ───────── Back navigation ─────────
// List pages keep their search and filters in the address (?q=…&status=…). The last address seen for each list is
// remembered for this browser tab, so "Back to Invoices" returns to the list exactly as it was left.
const LIST_KEY = "wgm.list:";
export function rememberListLocation(pathname: string, search: string) {
  if (!/^\/[a-z-]+$/.test(pathname)) return; // list pages are one segment: /invoices, /jobs, …
  try { sessionStorage.setItem(LIST_KEY + pathname, pathname + search); } catch { /* storage unavailable: plain link */ }
}
function rememberedListLocation(to: string) {
  if (!/^\/[a-z-]+$/.test(to)) return to;
  try { return sessionStorage.getItem(LIST_KEY + to) ?? to; } catch { return to; }
}

export type BackTarget = { to: string; label: string };
export function BackLink({ to, label }: BackTarget) {
  return (
    <Link to={rememberedListLocation(to)} className="back-link">
      <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3 5 8l5 5" /></svg>
      Back to {label}
    </Link>
  );
}

export function PageHeader({ title, sub, actions, back }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; back?: BackTarget }) {
  useEffect(() => { if (typeof title === "string") document.title = `${title} · Wali Garage Manager`; }, [title]);
  return (
    <div className="page-head">
      <div>
        {back && <BackLink {...back} />}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; pad?: boolean }) {
  return (
    <section className="card">
      {(title || actions) && <div className="card-head"><h2>{title}</h2>{actions && <div className="row">{actions}</div>}</div>}
      {pad ? <div className="card-body">{children}</div> : children}
    </section>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="loading" role="status"><span className="spin" style={{ width: 18, height: 18, border: "2px solid currentColor", borderRightColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite" }} />{label}</div>;
}

export function ErrorBox({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="alert error" role="alert">
      <strong>Couldn't load this</strong>
      {msg}
      {retry && <div style={{ marginTop: 8 }}><Button size="sm" onClick={retry}>Try again</Button></div>}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children && <p>{children}</p>}{action}</div>;
}

export function Alert({ kind = "info", title, children }: { kind?: "info" | "warn" | "error" | "success"; title?: string; children?: ReactNode }) {
  return <div className={`alert ${kind}`} role={kind === "error" ? "alert" : undefined}>{title && <strong>{title}</strong>}{children}</div>;
}

export function Badge({ tone, children }: { tone?: "blue" | "green" | "amber" | "red" | "navy"; children: ReactNode }) {
  return <span className={`badge ${tone ?? ""}`}>{children}</span>;
}

export function Reg({ reg }: { reg?: string | null }) {
  return reg ? <span className="reg-plate">{displayReg(reg)}</span> : <span className="muted">—</span>;
}

export function Money({ pence, className }: { pence: number | null | undefined; className?: string }) {
  return <span className={className} style={{ fontVariantNumeric: "tabular-nums" }}>{formatGBP(pence)}</span>;
}

// ───────── Form fields ─────────
export function Field({ label, hint, error, children, full, htmlFor }: { label: string; hint?: ReactNode; error?: string; children: ReactNode; full?: boolean; htmlFor?: string }) {
  return (
    <div className={`field ${full ? "full" : ""}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="err">{error}</span>}
    </div>
  );
}

export function TextInput({ label, hint, error, full, className = "", ...rest }: { label: string; hint?: ReactNode; error?: string; full?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return <Field label={label} hint={hint} error={error} full={full} htmlFor={id}><input id={id} className={`input ${error ? "invalid" : ""} ${className}`} {...rest} /></Field>;
}

export function MoneyInput({ label, hint, error, full, ...rest }: { label: string; hint?: ReactNode; error?: string; full?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} full={full} htmlFor={id}>
      <div className="input-prefix"><span>£</span><input id={id} inputMode="decimal" className={`input ${error ? "invalid" : ""}`} placeholder="0.00" {...rest} /></div>
    </Field>
  );
}

export function SelectInput({ label, hint, error, full, options, placeholder, ...rest }: { label: string; hint?: ReactNode; error?: string; full?: boolean; options: { value: string; label: string }[]; placeholder?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} full={full} htmlFor={id}>
      <select id={id} className={`input ${error ? "invalid" : ""}`} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

export function TextArea({ label, hint, error, full, ...rest }: { label: string; hint?: ReactNode; error?: string; full?: boolean } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return <Field label={label} hint={hint} error={error} full={full} htmlFor={id}><textarea id={id} className={`input ${error ? "invalid" : ""}`} {...rest} /></Field>;
}

/** Map server validation details to field errors. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (err instanceof ApiError && err.code === "validation" && Array.isArray(err.details)) {
    return Object.fromEntries(err.details.map((d: { path: string; message: string }) => [d.path, d.message]));
  }
  return {};
}

export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <div className="alert error" role="alert" style={{ marginTop: 12 }}><strong>Not saved</strong>{msg}</div>;
}

/** Navigation state for leaving a form because it was just saved (skips the unsaved-changes warning). */
export const AFTER_SAVE = { state: { afterSave: true } };

/** Warn before leaving the page with unsaved edits: closing/reloading the tab, and moving to another page in the app
 *  (Back buttons, the menu, the browser's back button). Changing filters on the same page is not "leaving". */
export function useUnsavedWarning(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirty && currentLocation.pathname !== nextLocation.pathname && !(nextLocation.state as { afterSave?: boolean } | null)?.afterSave);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("You have unsaved changes. Leave this page and lose them?")) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
}

// ───────── Modal ─────────
export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input, select, textarea, button:not([aria-label='Close'])");
    first?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus(); };
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" ref={ref}>
        <div className="modal-head"><h2 style={{ flex: 1 }}>{title}</h2><Button variant="ghost" className="icon-btn" aria-label="Close" onClick={onClose}>✕</Button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ───────── Table ─────────
export interface Column<T> { key: string; label: string; render: (row: T) => ReactNode; num?: boolean; className?: string }
export function DataTable<T>({ rows, columns, rowLink, empty, footer }: { rows: T[]; columns: Column<T>[]; rowLink?: (row: T) => string; empty?: ReactNode; footer?: ReactNode }) {
  const navigate = useNavigate();
  if (!rows.length) return <>{empty ?? <Empty title="Nothing here yet" />}</>;
  return (
    <div className="table-wrap">
      <table className="table responsive">
        <thead><tr>{columns.map((c) => <th key={c.key} className={c.num ? "num" : ""}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const href = rowLink?.(r);
            return (
              <tr key={i} className={href ? "clickable" : ""} tabIndex={href ? 0 : undefined}
                onClick={href ? (e) => { if (!(e.target as HTMLElement).closest("a,button")) navigate(href); } : undefined}
                onKeyDown={href ? (e) => { if (e.key === "Enter") navigate(href); } : undefined}>
                {columns.map((c) => <td key={c.key} data-label={c.label} className={`${c.num ? "num" : ""} ${c.className ?? ""}`}>{c.render(r)}</td>)}
              </tr>
            );
          })}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

export function Chips<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div className="filters" role="group">
      {options.map((o) => <button key={o.value} type="button" className={`chip ${value === o.value ? "active" : ""}`} aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode }[] }) {
  return <div className="tabs" role="tablist">{tabs.map((t) => <button key={t.value} role="tab" aria-selected={value === t.value} className={`tab ${value === t.value ? "active" : ""}`} onClick={() => onChange(t.value)}>{t.label}</button>)}</div>;
}

export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function penceToInput(p: number | null | undefined) {
  if (p === null || p === undefined) return "";
  return (p / 100).toFixed(2);
}
